import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Tiny Homes Project · automated · $500/month × 60 months to ownership
 * Solar + wind powered · four world problems solved by this page existing
 */
export default function TinyHomesPage() {
  const [manifest, setManifest] = useState(null);
  const [tally, setTally] = useState(null);
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/sage/tiny-homes/manifest`).then(r => r.json()).then(setManifest).catch(() => {});
    fetch(`${API}/api/sage/tiny-homes/tally`).then(r => r.json()).then(setTally).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#fbfaf6] text-[#1e1e1e]" data-testid="tiny-homes-page">
      {/* Status banner — Founder ruling 2026-08-11 */}
      <div className="bg-[#b58c2a] text-[#1e1e1e] py-3 px-6 text-center" data-testid="tiny-homes-status-banner">
        <p className="text-base md:text-lg font-serif font-bold uppercase tracking-wide">
          501(c)(3) Charity to Erase Homelessness at the County Level
        </p>
        <p className="text-sm font-serif italic mt-1">
          Awaiting overflow distribution from Sage · Resilience Studios · Mender · Purser
        </p>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="border-b-2 border-[#b58c2a] pb-4">
          <span className="text-4xl font-serif text-[#2c3e2a]">Tiny Homes Project</span>
          <span className="text-lg text-[#7c8a75] font-serif italic ml-3">a Missouri Nonprofit · four years filed · </span>
        </div>
        <p className="mt-6 text-2xl font-serif text-[#2c3e2a] leading-snug max-w-3xl">
          Five hundred dollars a month, sixty months, and the home is yours.
        </p>
        <p className="mt-3 text-base text-[#4a5a48] font-serif max-w-3xl">
          No bank. No credit check. No down payment. No payments for the first ninety days. No interest.
          No utility cost. No property taxes. Solar and wind powered. Delivered by the same family that
          feeds you at Rice's till and hires from the Brotherhood.
        </p>
        <button
          onClick={() => setShowApply(true)}
          className="mt-8 bg-[#2c3e2a] text-[#fbfaf6] py-3 px-8 rounded font-serif hover:bg-[#3e4a3c] transition"
          data-testid="apply-button"
        >
          Apply for a Tiny Home →
        </button>
      </section>

      {/* Living tally */}
      {tally && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="tally-strip">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="applications pending" value={tally.applications_pending || 0} testid="tally-pending" />
            <Tile label="tenants housed" value={tally.tenants_housed || 0} testid="tally-housed" />
            <Tile label="parcels secured" value={tally.parcels_secured || 0} testid="tally-parcels" />
            <Tile label="payments logged" value={tally.monthly_payments_logged || 0} testid="tally-payments" />
          </div>
          <p className="mt-3 text-xs font-serif italic text-[#7c8a75]">
            Every number above is the honest live count. Zero shows as zero because zero is the truth today.
          </p>
        </section>
      )}

      {/* The four world problems solved */}
      {manifest && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="four-problems">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            Four world problems solved by this page existing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(manifest.world_problems_solved || []).map((p, i) => (
              <div key={i} className="bg-white border border-[#d9d3bd] rounded p-5" data-testid={`problem-card-${i}`}>
                <div className="text-lg font-serif font-bold text-[#2c3e2a]">{p.problem}</div>
                <p className="mt-2 text-sm font-serif text-[#4a5a48]">{p.how}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Who qualifies */}
      {manifest && (
        <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="who-qualifies">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            Who qualifies
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-base font-serif text-[#4a5a48]">
            {(manifest.target_populations || []).map((s, i) => (
              <li key={i} className="before:content-['·'] before:text-[#b58c2a] before:mr-2">{s}</li>
            ))}
          </ul>
        </section>
      )}

      {/* The collapse math */}
      {manifest?.collapse_math && (
        <section className="max-w-6xl mx-auto px-6 pb-12" data-testid="collapse-math">
          <h2 className="text-2xl font-serif text-[#2c3e2a] border-l-4 border-[#b58c2a] pl-4 mb-4">
            How the automation collapses the traditional cost
          </h2>
          <div className="bg-[#2c3e2a] text-[#fbfaf6] rounded p-6 mb-5">
            <div className="text-lg font-serif">Traditional all-in cost per unit</div>
            <div className="text-3xl font-serif mt-1">
              ${manifest.collapse_math.traditional_all_in_per_unit_usd?.low?.toLocaleString()}
              {" – "}
              ${manifest.collapse_math.traditional_all_in_per_unit_usd?.high?.toLocaleString()}
            </div>
            <div className="text-lg font-serif mt-4">Automated all-in cost per unit</div>
            <div className="text-3xl font-serif mt-1 text-[#c8b78a]">
              ${manifest.collapse_math.automated_all_in_per_unit_usd?.low?.toLocaleString()}
              {" – "}
              ${manifest.collapse_math.automated_all_in_per_unit_usd?.high?.toLocaleString()}
            </div>
            <div className="text-base font-serif italic text-[#c8b78a] mt-4">
              A roughly {manifest.collapse_math.collapse_ratio_pct}% collapse. The same six-million-dollar county ask now
              delivers about {manifest.collapse_math.same_county_ask_units_after} units instead of {manifest.collapse_math.same_county_ask_units_before}.
            </div>
          </div>
          <div className="bg-white border border-[#d9d3bd] rounded overflow-hidden">
            <table className="w-full text-sm font-serif">
              <thead className="bg-[#efeadc] text-[#2c3e2a]">
                <tr>
                  <th className="text-left px-4 py-2">Component</th>
                  <th className="text-left px-4 py-2">Traditional</th>
                  <th className="text-left px-4 py-2">Automated</th>
                </tr>
              </thead>
              <tbody>
                {(manifest.collapse_math.line_items || []).map((row, i) => (
                  <tr key={i} className="border-t border-[#d9d3bd] align-top">
                    <td className="px-4 py-3 text-[#2c3e2a] font-bold w-1/4">{row.component}</td>
                    <td className="px-4 py-3 text-[#4a5a48]">{row.traditional_usd}</td>
                    <td className="px-4 py-3 text-[#4a5a48]">{row.automated_usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Apply modal */}
      {showApply && (
        <ApplyModal onClose={() => setShowApply(false)} />
      )}

      <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-[#d9d3bd]">
        <p className="text-2xl md:text-3xl font-serif font-bold text-[#2c3e2a] leading-snug">
          Every home also receives a bag of rice bi-annually.<br/>
          A tree is planted in every yard.<br/>
          <span className="text-[#b58c2a]">Same fire · different notes.</span>
        </p>
      </footer>
    </div>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#d9d3bd] rounded p-4" data-testid={testid}>
      <div className="text-xs font-serif italic text-[#7c8a75] uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-serif mt-1 text-[#2c3e2a]">{value}</div>
    </div>
  );
}

function ApplyModal({ onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", state: "",
    target_population: "", household_size: 1, story: "", preferred_county: "",
  });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.story.trim() || form.story.length < 20) {
      toast.error("Please share your name, email, and a short story (at least 20 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/sage/tiny-homes/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Something got in the way. Please try again.");
      setSubmitted(data);
      toast.success("Application filed.");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="apply-modal-backdrop"
    >
      <div
        className="bg-[#fbfaf6] rounded max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="apply-modal"
      >
        {submitted ? (
          <div>
            <h2 className="text-3xl font-serif text-[#2c3e2a] border-b-2 border-[#b58c2a] pb-3">Application filed.</h2>
            <p className="mt-4 text-base font-serif text-[#4a5a48]">{submitted.message}</p>
            {submitted.application_id && (
              <p className="mt-3 text-sm font-serif italic text-[#7c8a75]" data-testid="th-application-id">
                Your reference: {submitted.application_id}
              </p>
            )}
            <button
              onClick={onClose}
              className="mt-6 bg-[#2c3e2a] text-[#fbfaf6] py-2 px-6 rounded font-serif hover:bg-[#3e4a3c] transition"
              data-testid="close-modal-button"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h2 className="text-3xl font-serif text-[#2c3e2a] border-b-2 border-[#b58c2a] pb-3">
              Apply for a Tiny Home
            </h2>
            <p className="text-sm font-serif italic text-[#7c8a75]">
              No credit check. No down payment. $500 a month, 60 months, the home is yours.
            </p>
            <Field label="Your name">
              <input required type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-name" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Email">
                <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-email" />
              </Field>
              <Field label="Phone (optional)">
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-phone" />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="City">
                <input type="text" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-city" />
              </Field>
              <Field label="State">
                <input type="text" maxLength={5} value={form.state} onChange={(e) => setForm({...form, state: e.target.value})}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-state" />
              </Field>
              <Field label="Household size">
                <input type="number" min={1} max={20} value={form.household_size} onChange={(e) => setForm({...form, household_size: parseInt(e.target.value) || 1})}
                  className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-household-size" />
              </Field>
            </div>
            <Field label="Which population fits you best?">
              <select value={form.target_population} onChange={(e) => setForm({...form, target_population: e.target.value})}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-population">
                <option value="">— pick one —</option>
                <option>Citizens who no longer choose to be homeless</option>
                <option>Low-income families</option>
                <option>Citizens aged 65+</option>
                <option>Citizens who lost their homes to fire, flood, tornado, or other tragedy</option>
                <option>Citizens seeking to escape domestic violence immediately</option>
                <option>First-time homeowners aged 18-23</option>
              </select>
            </Field>
            <Field label="Your story (short paragraph)">
              <textarea required rows={4} value={form.story} onChange={(e) => setForm({...form, story: e.target.value})}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-story" />
            </Field>
            <Field label="Preferred county (optional)">
              <input type="text" value={form.preferred_county} onChange={(e) => setForm({...form, preferred_county: e.target.value})}
                className="w-full border border-[#d9d3bd] rounded px-3 py-2 font-serif bg-white" data-testid="input-th-preferred-county" />
            </Field>
            <div className="flex gap-3">
              <button type="submit" disabled={submitting}
                className="flex-1 bg-[#2c3e2a] text-[#fbfaf6] py-3 rounded font-serif hover:bg-[#3e4a3c] transition disabled:opacity-60"
                data-testid="submit-th-application">
                {submitting ? "Sending…" : "Send my application"}
              </button>
              <button type="button" onClick={onClose}
                className="border border-[#d9d3bd] py-3 px-6 rounded font-serif hover:border-[#2c3e2a] transition"
                data-testid="cancel-application">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-serif text-[#2c3e2a] mb-1">{label}</span>
      {children}
    </label>
  );
}
